/**
 * 编辑器偏好仓库。
 *
 * 设计说明：
 * 1. 承载所有绑定到单个项目的本地编辑器状态（snapSettings / editorDisplay / publishedVersion）；
 * 2. 主键是后端 projectId（路由 param），不依赖 Dexie 的递增 id；
 * 3. upsert 采用"合并补丁"语义：只覆盖补丁里给出的字段，其它字段保留原值；
 * 4. 这是替换旧 projectRepo.update(..., { snapSettings / config / publishedVersion }) 的唯一入口，
 *    调用方不需要、也不应该关心底层存储。
 */

import { db } from './schema';
import type { EditorPreferences } from '@puppycad/cad-core';
import {
  normalizeEditorPreferencesPatch,
  type EditorPreferencesPatch,
} from './editor-preferences-snapshot';

export class EditorPreferencesRepository {
  /**
   * 读取某个项目的偏好记录；不存在时返回 undefined。
   */
  async get(projectId: string): Promise<EditorPreferences | undefined> {
    return await db.editorPreferences.get(projectId);
  }

  /**
   * 合并写入偏好。
   * 存在的字段按补丁覆盖，未提供的字段保留原值。
   * 无论是否存在记录，都会刷新 updatedAt。
   */
  async upsert(projectId: string, patch: EditorPreferencesPatch): Promise<void> {
    const existing = await db.editorPreferences.get(projectId);
    const normalizedPatch = normalizeEditorPreferencesPatch(patch);
    const next: EditorPreferences = {
      ...(existing ?? { projectId }),
      ...normalizedPatch,
      projectId,
      updatedAt: Date.now(),
    };
    await db.editorPreferences.put(next);
  }

  /**
   * 删除某个项目的偏好记录。
   */
  async delete(projectId: string): Promise<void> {
    await db.editorPreferences.delete(projectId);
  }
}

export const editorPreferencesRepo = new EditorPreferencesRepository();
export default editorPreferencesRepo;
export type { EditorPreferencesPatch } from './editor-preferences-snapshot';
